import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { unsubscribe, checkOpenClawAccess, getBrowserTaskStatus, cancelBrowserTask, type UnsubscribeResult } from '@/api/messages';
import { MailX, X, CheckCircle2, AlertCircle, Loader2, Brain, WifiOff, ExternalLink } from 'lucide-react';
import { pollUntil } from '@/lib/poll';
import { ApiError } from '@/api/client';

// How long to wait for the browser agent before giving up and offering the page instead (B-20).
const BROWSER_TASK_TIMEOUT_MS = 10 * 60 * 1000;

interface UnsubscribeModalProps {
  mailboxId: string;
  uid: number;
  folder?: string | null;
  fromEmail: string;
  fromName?: string;
  subject?: string;
  onClose: () => void;
}

type Phase = 'idle' | 'fetching' | 'browser-pending' | 'done' | 'error' | 'cancelled';

export function UnsubscribeModal({ mailboxId, uid, folder, fromEmail, fromName, subject, onClose }: UnsubscribeModalProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<UnsubscribeResult | null>(null);
  const [browserStatus, setBrowserStatus] = useState<string>('');

  const llmCheck = useQuery({
    queryKey: ['openclaw-access'],
    queryFn: checkOpenClawAccess,
    staleTime: 30_000,
  });

  const unsubMutation = useMutation({
    mutationFn: async () => {
      setPhase('fetching');
      const res = await unsubscribe(mailboxId, uid, folder);
      return res;
    },
    onSuccess: (res) => {
      setResult(res);
      // A browser hand-off isn't finished yet, even when the first page answered 200.
      if (res.result?.browserTaskId) {
        setPhase('browser-pending');
        setBrowserStatus('Handing off to browser agent…');
      } else if (res.ok) {
        setPhase('done');
      } else {
        setPhase('error');
      }
    },
    onError: (err: Error) => {
      setResult({ ok: false, error: err.message });
      setPhase('error');
    }
  });

  // Fire unsubscribe on mount
  useEffect(() => {
    if (phase === 'idle' && !unsubMutation.isPending && !unsubMutation.isError) {
      unsubMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for browser task status when handed off
  useEffect(() => {
    if (phase !== 'browser-pending' || !result?.result?.browserTaskId) return;
    const taskId = result.result.browserTaskId;

    // Settles on a final status; gives up after BROWSER_TASK_TIMEOUT_MS; stops when the modal closes.
    return pollUntil(async () => {
      const status = await getBrowserTaskStatus(taskId).catch((err) => {
        if (err instanceof ApiError && err.status === 404) return null; // gone: nothing left to wait for
        throw err;
      });
      if (!status) {
        setResult((prev) => prev ? { ...prev, ok: false, error: 'The browser task is no longer available.' } : prev);
        setPhase('error');
        return true;
      }
      if (status.status === 'pending') {
        setBrowserStatus('Waiting for browser agent…');
      } else if (status.status === 'processing') {
        setBrowserStatus('Browser agent navigating to confirmation page…');
      } else if (status.status === 'completed') {
        if (status.result?.success) {
          setResult((prev) => prev ? { ...prev, ok: true } : prev);
          setPhase('done');
        } else {
          setResult((prev) => prev ? { ...prev, ok: false, error: status.result?.message || status.error || 'Browser agent could not complete' } : prev);
          setPhase('error');
        }
        return true;
      } else if (status.status === 'failed') {
        setResult((prev) => prev ? { ...prev, ok: false, error: status.error || 'Browser agent failed' } : prev);
        setPhase('error');
        return true;
      } else if (status.status === 'cancelled') {
        setPhase('cancelled');
        return true;
      }
      return false;
    }, {
      intervalMs: 3000,
      timeoutMs: BROWSER_TASK_TIMEOUT_MS,
      onTimeout: () => {
        cancelBrowserTask(taskId).catch(() => {});
        setResult((prev) => prev ? { ...prev, ok: false, error: 'The browser agent did not finish within 10 minutes.' } : prev);
        setPhase('error');
      },
    });
  }, [phase, result?.result?.browserTaskId]);

  // User escape hatch — stops waiting and tells the queue to stop retrying.
  const onCancel = () => {
    const taskId = result?.result?.browserTaskId;
    if (taskId) cancelBrowserTask(taskId).catch(() => {});
    setPhase('cancelled');
  };

  const llm = llmCheck.data;
  const gatewayUp = llm?.gateway;
  const isRunning = unsubMutation.isPending || phase === 'fetching' || phase === 'browser-pending';
  // The page that needed interaction, offered whenever the automatic route didn't finish (B-20).
  const needsManualFollow = (phase === 'error' || phase === 'cancelled') ? result?.result?.manualFollowUrl : undefined;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget && !isRunning) onClose(); }}>
      {/* Cancel is always reachable while running — a looping browser task must never trap the user */}
      <div className="w-full max-w-md rounded-xl border border-bmail-border bg-bmail-surface p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-bmail-text">
            <MailX size={20} /> Unsubscribe
          </h2>
          {!isRunning && (
            <button onClick={onClose} className="text-bmail-text-secondary hover:text-bmail-text">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Email info */}
        <div className="mb-4 rounded-lg border border-bmail-border bg-bmail-navy/30 p-3 text-sm">
          <div className="text-bmail-text-secondary">From: <span className="text-bmail-text">{fromName ? `${fromName} ` : ''}&lt;{fromEmail}&gt;</span></div>
          {subject && <div className="mt-1 text-bmail-text-secondary">Subject: <span className="text-bmail-text">{subject}</span></div>}
        </div>

        {/* OpenClaw gateway check */}
        <div className="mb-4">
          <div className="flex items-center gap-2 text-sm">
            {/* While this loads, the connection pill is the loading indicator. */}
            {llm && gatewayUp && <><Brain size={14} className="text-green-500" /> <span className="text-bmail-text">OpenClaw gateway available — browser escalation ready</span></>}
            {llm && !gatewayUp && <><WifiOff size={14} className="text-bmail-warning" /> <span className="text-bmail-warning">OpenClaw gateway unreachable — browser escalation unavailable</span></>}
            {llmCheck.isError && <><AlertCircle size={14} className="text-bmail-error" /> <span className="text-bmail-error">Cannot reach OpenClaw</span></>}
          </div>
        </div>

        {/* Status / progress */}
        <div className="space-y-3">
          {phase === 'fetching' && (
            <div className="flex items-center gap-2 text-sm text-bmail-text-secondary">
              <Loader2 size={16} className="animate-spin" /> Searching email for unsubscribe links…
            </div>
          )}

          {phase === 'browser-pending' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-bmail-gold">
                <Loader2 size={16} className="animate-spin" /> {browserStatus}
              </div>
              <div className="text-xs text-bmail-text-secondary">
                The unsubscribe page needs interaction (button click or form). Frances is handling it in the browser — this usually takes 10-30 seconds.
              </div>
            </div>
          )}

          {phase === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-green-500">
                <CheckCircle2 size={18} /> Unsubscribed successfully
              </div>
              {result.links && result.links.length > 0 && (
                <div className="text-xs text-bmail-text-secondary">
                  Method: {result.followed?.type === 'mailto' ? 'Sent unsubscribe email' : `Followed link${result.result?.browserTaskId ? ' + browser confirmation' : ''}`}
                </div>
              )}
            </div>
          )}

          {phase === 'error' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-bmail-error">
                <AlertCircle size={18} /> Could not unsubscribe
              </div>
              <div className="text-xs text-bmail-text-secondary">
                {result.error || result.result?.error || 'Unknown error'}
              </div>
            </div>
          )}

          {phase === 'cancelled' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-bmail-warning">
                <AlertCircle size={18} /> Cancelled
              </div>
              <div className="text-xs text-bmail-text-secondary">
                The unsubscribe attempt was stopped. If the page was mid-confirmation it may or may not have gone through — check your inbox for a confirmation email.
              </div>
            </div>
          )}

          {/* Manual follow fallback — the browser agent failed, timed out or was cancelled */}
          {needsManualFollow && (
            <div className="rounded-lg border border-bmail-warning/30 bg-bmail-warning/10 p-3">
              <div className="mb-1 flex items-center gap-2 text-sm text-bmail-warning">
                <AlertCircle size={14} /> Page needs manual confirmation
              </div>
              <p className="mb-2 text-xs text-bmail-text-secondary">
                The unsubscribe page requires interaction. Open it to complete manually:
              </p>
              <a href={needsManualFollow} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-bmail-gold hover:underline break-all">
                <ExternalLink size={12} className="shrink-0" /> {needsManualFollow}
              </a>
            </div>
          )}

          {/* Fallback links */}
          {phase === 'error' && result?.links && result.links.length > 0 && (
            <div className="mt-2 rounded-lg border border-bmail-border p-2">
              <div className="mb-1 text-xs font-medium text-bmail-text">Found links (auto-follow failed):</div>
              {result.links.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs text-bmail-gold hover:underline">
                  {l.url}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-2">
          {isRunning && (
            <button onClick={onCancel} className="rounded-lg border border-bmail-warning/40 bg-bmail-warning/10 px-4 py-2 text-sm font-medium text-bmail-warning hover:bg-bmail-warning/20">
              Cancel
            </button>
          )}
          {!isRunning && (
            <button onClick={onClose} className="rounded-lg border border-bmail-border px-4 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover">
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}